package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupVisibility
import com.swetdot.swetgram.domain.GroupVisibilityChoice
import com.swetdot.swetgram.domain.NewGroup
import com.swetdot.swetgram.protocol.Constraints
import com.swetdot.swetgram.protocol.dto.ValidationCodes
import com.swetdot.swetgram.ui.auth.FieldError
import com.swetdot.swetgram.ui.auth.validateUsername
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * Creating a group -- SPS §12.2, and the contract's `createGroup`.
 *
 * WHAT A GROUP REQUIRES AT CREATION, AND WHAT CAN WAIT
 *
 * `CreateGroupRequest` marks exactly two properties `required`: `name` and `visibility`. Everything
 * else -- description, avatar, seeded members, and the username when the group is private -- is
 * optional. But "optional" is not the axis that matters. The axis is whether the choice can be
 * unmade:
 *
 *  - `name`, `description`, `username` and the avatar are all offered again by `UpdateGroupRequest`
 *    (`domain.GroupEdit`). A mistake in any of them is a rename away from being fixed.
 *  - `visibility` is NOT offered by `UpdateGroupRequest`, and pass 1 recorded that the omission is
 *    deliberate rather than an oversight (DECISIONS §7: flipping private → public "retroactively
 *    exposes the Discover area and the member list", and the reverse strands the username and every
 *    QR code printed from it). THIS SCREEN IS THE ONLY PLACE IT CAN EVER BE CHOSEN.
 *
 * So the form asks for a name, a description, and a visibility it will not let anybody drift past.
 *
 * TWO THINGS SPS §12.2 AND THE CONTRACT OFFER THAT THIS PASS DOES NOT BUILD, said plainly rather
 * than left to be discovered:
 *
 *  - The OPTIONAL AVATAR. §12.2 lists it among the creation fields, so leaving it out is a
 *    deviation from a binding document. It is deferred because `updateGroup` takes
 *    `avatar_media_id` too, so nothing is lost permanently, and because wiring the picker and the
 *    upload reaches files outside this pass. `NewGroup.avatarMediaId` is left at its default.
 *  - `initial_member_ids`. §12.2 does not list it -- the spec's own creation fields are name,
 *    avatar and description -- but it is worth naming what deferring it costs, because unlike the
 *    avatar it is NOT reversible in the same way: v1.0.0 has no add-member operation at all
 *    (`domain/Group.kt` records the probe), and the only other way in is `POST /join/{invite_token}`,
 *    which pass 3 owns. Until then a group created here has exactly one member. That is a real gap
 *    and it is stated, not hidden.
 *
 * VALIDATION HAPPENS BEFORE THE ROUND TRIP because the round trip is not free. DECISIONS §12 makes
 * group creation 5/day/user and the unit "is consumed before the body is read" -- verified against
 * the deployed server on 2026-08-06, which answered `429 rate_limit_exceeded` ("You have created
 * too many groups today.") to bodies it never validated. A form that let a three-character mistake
 * through would spend a fifth of somebody's day on it.
 *
 * Follows this package's neighbours: an androidx `ViewModel`, work on `viewModelScope`, state as a
 * `StateFlow`, `CancellationException` rethrown first, and failures shown alongside the form.
 */
class CreateGroupViewModel(
    private val groups: GroupRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(CreateGroupUiState())
    val state: StateFlow<CreateGroupUiState> = _state.asStateFlow()

    /**
     * Types into any field.
     *
     * The whole form travels rather than one field at a time, so there is one place the state can
     * be wrong instead of four. Server-named errors for the fields that actually changed are
     * dropped on the way through -- see [clearStaleFieldMessages].
     */
    fun onFormChange(next: CreateGroupForm) {
        val current = _state.value
        _state.value = current.copy(
            form = next,
            fieldMessages = clearStaleFieldMessages(current.form, next, current.fieldMessages),
        )
    }

    /**
     * Sends it, once.
     *
     * The in-flight guard is not politeness. `createGroup` carries no `Idempotency-Key` and
     * `CreateGroupRequest` no `client_id`, so the app's idempotency convention does not reach this
     * operation at all -- `GroupRepository.create` says the same thing from the other side, and
     * never retries. A second tap is a second group AND a second unit of a five-a-day budget. SDS
     * §25.1 states the rule ("Loading state should prevent duplicate actions"); here it is the
     * difference between one group and two.
     *
     * A form that does not validate sets [CreateGroupUiState.submitted] and stops. Nothing is sent
     * and nothing is spent; the screen starts showing what is wrong.
     */
    fun submit() {
        val current = _state.value
        if (!current.canSubmit) return

        val draft = current.form.toNewGroup()
        if (draft == null) {
            _state.value = current.copy(submitted = true)
            return
        }

        _state.value = current.copy(
            submitted = true,
            submitting = true,
            // Both cleared: the previous attempt's verdict is about a body that is no longer the
            // one being sent, and leaving "another group already uses that username" under a name
            // the user has since changed is the failure `clearStaleFieldMessages` exists to
            // prevent, one submit later.
            banner = null,
            fieldMessages = emptyMap(),
        )

        viewModelScope.launch {
            try {
                val created = groups.create(draft)
                _state.value = _state.value.copy(submitting = false, created = created)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                val failure = createGroupFailure(error)
                _state.value = _state.value.copy(
                    submitting = false,
                    banner = failure.banner,
                    fieldMessages = failure.fieldMessages,
                )
            }
        }
    }

    /** The caller opens the new group and then calls this, so it cannot navigate twice. */
    fun consumeCreated() {
        _state.value = _state.value.copy(created = null)
    }

    fun dismissBanner() {
        _state.value = _state.value.copy(banner = null)
    }
}

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

/**
 * The create form as a value, so every decision on this screen is a function of its inputs and can
 * be tested without a renderer -- the arrangement `ChatViewModelTest` argues for and this package's
 * other view models already follow.
 *
 * [created] is a one-shot navigation signal rather than screen state, exactly like
 * `GroupUiState.exited`; see [CreateGroupViewModel.consumeCreated].
 */
data class CreateGroupUiState(
    val form: CreateGroupForm = CreateGroupForm(),
    /**
     * Field errors the SERVER named, keyed by the contract's own field names (`name`,
     * `description`, `visibility`, `username`).
     *
     * Kept apart from [errors], which is what the client worked out for itself. The two are shown
     * in the same place and the server's wins, because it knows things this device cannot -- that a
     * username is taken, that the account's email is not verified yet.
     */
    val fieldMessages: Map<String, String> = emptyMap(),
    /**
     * True once Create has been pressed at least once.
     *
     * Local errors are hidden until then. A form that turns red while somebody is still typing
     * their group's name is accusing them of a mistake they have not finished not making.
     */
    val submitted: Boolean = false,
    val submitting: Boolean = false,
    /** A failure about the REQUEST rather than about a field. Shown above the form. */
    val banner: String? = null,
    val created: Group? = null,
) {
    /** What the client can tell without asking. Recomputed; it is four length checks. */
    val errors: CreateGroupErrors get() = validateCreateGroup(form)

    /**
     * The button is live unless a request is already running.
     *
     * Deliberately NOT "the form is valid". A Create button disabled until every box is right
     * leaves a user tapping a dead control with no statement of what is missing -- and the one
     * thing this screen most needs to say, that a visibility has not been chosen, would be
     * invisible. Pressing it is what reveals the problems.
     */
    val canSubmit: Boolean get() = !submitting
}

/**
 * The form's contents.
 *
 * [visibility] is `domain.GroupVisibility?` rather than `domain.GroupVisibilityChoice`, and the
 * difference is the form's whole job. `GroupVisibilityChoice` makes the illegal states
 * unrepresentable -- a public group carries its username inside the choice -- which is exactly
 * right for a REQUEST and impossible for a form, where the user types a username before or after
 * picking, and where "not decided yet" is a state that must exist. The two are joined by
 * [toNewGroup], which is the only place a choice is built.
 *
 * IT STARTS NULL, and that is the most deliberate thing on this screen. There is no safe default:
 * private is not the cautious option, because a private group can never become public either --
 * `UpdateGroupRequest` omits visibility in BOTH directions. Whichever way it defaulted, somebody
 * who did not read the section would end up permanently on the wrong side of a decision the app
 * made for them. So the app does not make it.
 */
data class CreateGroupForm(
    val name: String = "",
    val description: String = "",
    val visibility: GroupVisibility? = null,
    /** Public groups only; ignored, and never sent, when [visibility] is private. */
    val username: String = "",
)

/** What is wrong with a field, as a reason rather than a sentence -- see `createGroupProblemText`. */
enum class CreateGroupProblem {
    NameRequired,
    NameTooLong,
    DescriptionTooLong,
    VisibilityNotChosen,
    UsernameRequired,
    UsernameTooShort,
    UsernameTooLong,
    UsernameBadCharacters,
    UsernameReserved,
}

data class CreateGroupErrors(
    val name: CreateGroupProblem? = null,
    val description: CreateGroupProblem? = null,
    val visibility: CreateGroupProblem? = null,
    val username: CreateGroupProblem? = null,
) {
    val isValid: Boolean
        get() = name == null && description == null && visibility == null && username == null
}

// ---------------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------------

/*
 * `Group.name` is `minLength: 1, maxLength: 100` and `Group.description` is `maxLength: 500` in the
 * frozen contract. They are restated here because `protocol.Constraints` has no entry for either --
 * it covers usernames, passwords, display names, bios and messages, and nothing about groups.
 *
 * TODO(protocol): these belong in `Constraints` beside the others, so the client and the server
 * mirror one number rather than keeping two copies of it. `:protocol` was not this pass's to edit.
 */
internal const val GROUP_NAME_MAX = 100
internal const val GROUP_DESCRIPTION_MAX = 500

/**
 * Everything decidable on the device, checked in one pass, so a user fixing one field at a time is
 * not made to make one round trip per mistake -- the same reason the error envelope carries an
 * ARRAY of `validation_errors`.
 *
 * The username rules are NOT restated. `ui/auth/validateUsername` is reused verbatim, which is the
 * decision the server made for the same reason: `ProfileValidation.validateUsername` and one shared
 * `Constraints.RESERVED_USERNAMES` serve users and public groups alike, "because a name reserved
 * for users and free for groups is a rule anyone can route around" (DECISIONS §7). The RULES are
 * shared; the WORDS are not -- see `createGroupProblemText`, which says nothing about people,
 * because the two namespaces are separate and `@alex` the person and `@alex` the group can coexist.
 */
internal fun validateCreateGroup(form: CreateGroupForm): CreateGroupErrors {
    // Validated as it will be SENT. Checking the raw input would reject a name typed with a
    // trailing space, and would judge a username on capitals the request drops anyway.
    val name = form.name.trim()
    val description = form.description.trim()

    return CreateGroupErrors(
        name = when {
            name.isEmpty() -> CreateGroupProblem.NameRequired
            name.length > GROUP_NAME_MAX -> CreateGroupProblem.NameTooLong
            else -> null
        },
        description = when {
            description.length > GROUP_DESCRIPTION_MAX -> CreateGroupProblem.DescriptionTooLong
            else -> null
        },
        visibility = if (form.visibility == null) CreateGroupProblem.VisibilityNotChosen else null,
        // A private group is not asked for a username and is not judged on one it may still be
        // carrying from before the choice was flipped -- that value is DROPPED by [toNewGroup]
        // rather than reported here, so switching to private cannot leave an error under a box
        // that is no longer on screen.
        username = when (form.visibility) {
            GroupVisibility.PUBLIC -> usernameProblem(form.username)
            GroupVisibility.PRIVATE, null -> null
        },
    )
}

/** The shared username rule, re-labelled for a group. */
private fun usernameProblem(raw: String): CreateGroupProblem? =
    when (validateUsername(normalizeGroupUsername(raw))) {
        null -> null
        FieldError.Required -> CreateGroupProblem.UsernameRequired
        FieldError.UsernameTooShort -> CreateGroupProblem.UsernameTooShort
        FieldError.UsernameTooLong -> CreateGroupProblem.UsernameTooLong
        FieldError.UsernameReserved -> CreateGroupProblem.UsernameReserved
        FieldError.UsernameBadCharacters -> CreateGroupProblem.UsernameBadCharacters
        // `validateUsername` returns only the five above. An exhaustive `when` over the whole enum
        // would be noise; a reason added to it later lands here, which is a wrong-but-visible
        // message rather than a silent pass.
        else -> CreateGroupProblem.UsernameBadCharacters
    }

/**
 * What actually goes on the wire: trimmed and lowercased, exactly as `GroupRepository.create` and
 * the server's own `normalizeUsername` both do it. Normalising in one direction only means the name
 * the user is told is unavailable is the name that was looked up.
 */
internal fun normalizeGroupUsername(raw: String): String = raw.trim().lowercase()

/**
 * The form as a request, or null when it is not one yet.
 *
 * This is where the form's three-state visibility becomes the domain's two-state
 * [GroupVisibilityChoice], and two contract rules are satisfied by construction rather than by
 * remembering them at a call site:
 *
 *  - a PRIVATE group's username is dropped, however much of one the user typed before switching.
 *    `GroupVisibilityChoice.Private` has nowhere to put one. The contract's `if`/`then` says a
 *    private group's username must be absent, and the server answers `400 validation_failed` with
 *    `{"field":"username","code":"invalid_format"}` -- which would cost one of the day's five
 *    creations for a box the user cannot even see.
 *  - a PUBLIC group cannot be built without one, because `Public` requires it.
 *
 * A blank description becomes ABSENT rather than an empty string: `""` is a value, the contract's
 * description is `oneOf [string, null]`, and describing a group as nothing is not the same as
 * declining to describe it. (`GroupRepository.create` does this too; doing it here as well means
 * the draft a test inspects is the request that would be sent.)
 *
 * `avatarMediaId` and `initialMemberIds` keep `NewGroup`'s defaults -- see the class note for what
 * that costs and why.
 */
internal fun CreateGroupForm.toNewGroup(): NewGroup? {
    if (!validateCreateGroup(this).isValid) return null
    val choice = when (visibility) {
        GroupVisibility.PUBLIC -> GroupVisibilityChoice.Public(normalizeGroupUsername(username))
        GroupVisibility.PRIVATE -> GroupVisibilityChoice.Private
        null -> return null
    }
    return NewGroup(
        name = name.trim(),
        visibility = choice,
        description = description.trim().takeIf { it.isNotEmpty() },
    )
}

/**
 * Drops the server's verdict on the fields that have since been edited.
 *
 * Per field rather than wholesale: "another group already uses that username" must survive the user
 * fixing their group's NAME, because it is still true, and it must not survive them changing the
 * username, because it is then a statement about a value nobody has asked about.
 *
 * Pure, and tested, because getting it wrong is invisible on screen: an error that lingers looks
 * exactly like an error that was just returned.
 */
internal fun clearStaleFieldMessages(
    previous: CreateGroupForm,
    next: CreateGroupForm,
    messages: Map<String, String>,
): Map<String, String> {
    if (messages.isEmpty()) return messages
    val kept = messages.toMutableMap()
    if (next.name != previous.name) kept -= FIELD_NAME
    if (next.description != previous.description) kept -= FIELD_DESCRIPTION
    if (next.visibility != previous.visibility) {
        kept -= FIELD_VISIBILITY
        // The username's fate is tied to visibility: switching to private hides the box and
        // switching back re-asks a question whose old answer was about the previous attempt.
        kept -= FIELD_USERNAME
    }
    if (next.username != previous.username) kept -= FIELD_USERNAME
    return kept
}

/** The contract's own field names, as they arrive in `validation_errors`. */
internal const val FIELD_NAME = "name"
internal const val FIELD_DESCRIPTION = "description"
internal const val FIELD_VISIBILITY = "visibility"
internal const val FIELD_USERNAME = "username"

// ---------------------------------------------------------------------------------------------
// Failures
// ---------------------------------------------------------------------------------------------

/**
 * A refused creation, split the way the screen shows it: [banner] over the form, [fieldMessages]
 * under the boxes they belong to.
 *
 * Both, rather than one or the other, because the two answer different questions. "Another group
 * already uses that username" belongs under the username box where it can be acted on; "you can
 * create five groups a day" belongs nowhere near a field, because no field is wrong.
 */
internal data class CreateGroupFailure(
    val banner: String? = null,
    val fieldMessages: Map<String, String> = emptyMap(),
)

/**
 * Turns whatever `createGroup` threw into something the form can show.
 *
 * The coarse branch is `asGroupFailure`, the boundary's own mapping, so this file never compares
 * error CODES and never imports `protocol.dto.ErrorCodes` (IMPLEMENTATION_NOTES §10 -- the same
 * arrangement `groupBanner` uses). What it does read from the underlying `ApiError` is the
 * `validation_errors` array, which carries field names no enum can express; `ui/auth/AuthState.kt`
 * unpacks the same array for the same reason.
 *
 * SDS §18.2 governs every sentence: say what happened and what to do next, never the raw error. Two
 * are worth reading twice.
 *
 * THE TIMEOUT does not claim the group was not created. There is no idempotency key, so a request
 * that timed out may well have succeeded with the client never hearing about it. "Could not create
 * the group" would be a guess, and a user acting on it makes a second group -- and spends a second
 * unit of the daily budget. The sentence sends them to look instead.
 *
 * THE 429 NAMES THE NUMBER. DECISIONS §12 sets group creation at 5/day/user, and the deployed
 * server's own message is "You have created too many groups today." -- true, and it leaves the user
 * wondering how many is too many and how long they have to wait.
 */
// TODO(localization): every string in this function moves into resources for EN + RU.
internal fun createGroupFailure(error: Throwable): CreateGroupFailure =
    when (error.asGroupFailure()) {
        // The username is taken, or reserved by a name released in the last 30 days. Either way it
        // belongs under the box, where changing it is one keystroke away.
        GroupFailure.USERNAME_TAKEN -> CreateGroupFailure(
            fieldMessages = mapOf(FIELD_USERNAME to USERNAME_TAKEN_TEXT),
        )

        GroupFailure.INVALID -> validationFailure(error as? ApiError.Validation)

        // `createGroup` is `x-requires-verified-email: true`. The user has an account, so the way
        // out is their inbox, not this screen.
        GroupFailure.EMAIL_NOT_VERIFIED -> CreateGroupFailure(
            banner = "Verify your email address before creating a group.",
        )

        GroupFailure.RATE_LIMITED -> CreateGroupFailure(
            banner = "You can create $GROUPS_PER_DAY groups a day. Try again tomorrow.",
        )

        // A group username changed within the last 30 days. Not reachable from THIS screen -- a
        // creation has no previous username -- but the boundary can produce it, and a `when` that
        // quietly folded it into the fallback would show "please try again" for something no
        // retry fixes.
        GroupFailure.USERNAME_COOLDOWN -> CreateGroupFailure(
            fieldMessages = mapOf(FIELD_USERNAME to "That username was used recently. Try another."),
        )

        // Everything with no group meaning -- offline, a timeout, an expired session, an upgrade
        // demand, a 5xx -- plus the group failures that belong to other operations.
        else -> CreateGroupFailure(banner = requestBanner(error))
    }

/**
 * The request-level sentence for anything `asGroupFailure` calls [GroupFailure.OTHER].
 *
 * Not `profileMessage`, which `groupBanner` delegates to: that table is written for a screen that
 * READS, and its offline and timeout lines say nothing about whether a write landed. Creation is a
 * write with no idempotency key, which makes the difference between "nothing was sent" and "we do
 * not know" the most important thing on the screen.
 */
private fun requestBanner(error: Throwable): String = when (error) {
    is ApiError.Offline -> "No connection. The group has not been created."
    // Deliberately not "it failed". See the note on [createGroupFailure].
    is ApiError.Timeout -> "The server took too long to answer. Check your groups before trying again."
    is ApiError.UpgradeRequired -> "Please update Swetgram."
    else -> "Could not create the group. Please try again."
}

/**
 * `400 validation_failed`, unpacked onto the fields the server named.
 *
 * A code this client does not recognise still SURFACES, against the field it arrived on, rather
 * than being dropped -- `AuthState.toFieldErrors` takes the same view. The alternative is a button
 * that does nothing with no explanation anywhere on the screen, which is how a rule added
 * server-side becomes an unreproducible bug report.
 *
 * If nothing could be attached to a field at all -- including the case where the error was not an
 * `ApiError.Validation` after all -- the banner carries it. An unattributed refusal with no visible
 * cause is the one case where the form has to say something general rather than nothing.
 */
private fun validationFailure(error: ApiError.Validation?): CreateGroupFailure {
    val messages = error?.errors
        ?.mapNotNull { item -> validationFieldText(item.field, item.code)?.let { item.field to it } }
        ?.toMap()
        .orEmpty()
    return if (messages.isEmpty()) {
        CreateGroupFailure(banner = "Please check the form and try again.")
    } else {
        CreateGroupFailure(fieldMessages = messages)
    }
}

/*
 * `ValidationCodes` is a table of constants rather than a wire type, which is why importing it here
 * does not put a DTO in the UI -- `ui/auth/FormValidation.kt` reads `protocol.Constraints` on the
 * same footing, and the alternative is re-spelling "too_long" as a literal where nothing keeps it
 * in step with the contract.
 *
 * TODO(boundary): the field-code vocabulary would be better mapped in `data/groups`, next to
 * `asGroupFailure`, so no UI file mentions `protocol` at all. That file was not this pass's.
 */
private fun validationFieldText(field: String, code: String): String? = when (field) {
    FIELD_NAME -> when (code) {
        ValidationCodes.TOO_SHORT, ValidationCodes.REQUIRED -> "Give the group a name."
        ValidationCodes.TOO_LONG -> "That name is too long. $GROUP_NAME_MAX characters at most."
        else -> UNRECOGNISED_FIELD_TEXT
    }
    FIELD_DESCRIPTION -> when (code) {
        ValidationCodes.TOO_LONG ->
            "That description is too long. $GROUP_DESCRIPTION_MAX characters at most."
        else -> UNRECOGNISED_FIELD_TEXT
    }
    FIELD_VISIBILITY -> "Choose who can join. This cannot be changed later."
    FIELD_USERNAME -> when (code) {
        ValidationCodes.REQUIRED -> "A public group needs a username."
        ValidationCodes.TOO_SHORT ->
            "Group usernames are at least ${Constraints.USERNAME_MIN} characters."
        ValidationCodes.TOO_LONG ->
            "Group usernames are at most ${Constraints.USERNAME_MAX} characters."
        ValidationCodes.USERNAME_TAKEN -> USERNAME_TAKEN_TEXT
        ValidationCodes.USERNAME_RESERVED -> "That username is reserved and cannot be used."
        else -> "Use lowercase letters, numbers and underscores only."
    }
    // A field this screen does not draw -- `avatar_media_id`, `initial_member_ids`. Neither is on
    // the form, so there is no box to hang it under and pinning it to one would be a lie. Null
    // sends it to the banner.
    else -> null
}

/**
 * THE SENTENCE SAYS NOTHING ABOUT PEOPLE, and that is the point.
 *
 * DECISIONS §7 makes public-group usernames a SEPARATE NAMESPACE from user usernames and states the
 * accepted cost outright: "`@alex` the person and `@alex` the group can coexist". The registration
 * form's "That username is not available" would be read here as a claim about somebody's account,
 * and a user who believed it would go looking for a person to blame for a name that is actually
 * free.
 */
private const val USERNAME_TAKEN_TEXT = "Another group already uses that username."

/** Visible rather than swallowed; see [validationFailure]. */
private const val UNRECOGNISED_FIELD_TEXT = "Swetgram cannot accept this value."

/** DECISIONS §12: group creation is 5/day/user, and the contract declares the `429` for it. */
private const val GROUPS_PER_DAY = 5
